import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogIn, LogOut, RefreshCw } from "lucide-react";
import { authLogin, authMe, authLogout, listEnquiries, updateEnquiryStatus } from "../lib/api";
import { Seo } from "../components/Seo";

export function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [authed, setAuthed] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (localStorage.getItem("speca_admin_token")) {
      authMe().then(() => setAuthed(true)).catch(() => authLogout());
    }
  }, []);
  if (authed) return <Navigate to="/admin/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await authLogin(email, password); nav("/admin/dashboard"); }
    catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <>
      <Seo title="Admin" path="/admin" />
      <section className="pt-40 pb-24 min-h-screen">
        <div className="max-w-md mx-auto px-6">
          <span className="overline">SPECA Admin</span>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-4">Sign in.</h1>
          <form onSubmit={submit} className="glass-card p-6 md:p-8 mt-8 space-y-5">
            <div>
              <label className="overline block mb-2">Email</label>
              <input data-testid="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[#0F0F0F] border border-white/15 text-white h-12 px-3 focus:outline-none focus:ring-1 focus:ring-silver" />
            </div>
            <div>
              <label className="overline block mb-2">Password</label>
              <input data-testid="admin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-[#0F0F0F] border border-white/15 text-white h-12 px-3 focus:outline-none focus:ring-1 focus:ring-silver" />
            </div>
            <button data-testid="admin-submit" disabled={loading} className="w-full bg-silver hover:bg-white text-black py-3.5 uppercase tracking-widest text-xs font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={14} />} Sign In
            </button>
          </form>
        </div>
      </section>
    </>
  );
}

const STATUS_OPTIONS = ["new", "contacted", "confirmed", "completed", "archived"];

export function AdminDashboard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const nav = useNavigate();

  const refresh = async () => {
    try { setLoading(true); const data = await listEnquiries(); setItems(data); }
    catch (e) {
      if (e.response?.status === 401) { authLogout(); nav("/admin"); }
      else toast.error("Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const changeStatus = async (id, status) => {
    try { await updateEnquiryStatus(id, status); setItems((cur) => cur.map((e) => e.id === id ? { ...e, status } : e)); }
    catch { toast.error("Update failed"); }
  };

  const visible = filter === "all" ? items : items.filter((e) => (e.status || "new") === filter);

  return (
    <>
      <Seo title="Admin Dashboard" path="/admin/dashboard" />
      <section className="pt-28 md:pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div>
              <span className="overline">SPECA Admin</span>
              <h1 className="font-display text-3xl md:text-4xl font-bold mt-3">Booking Requests</h1>
              <p className="text-gray-400 text-sm mt-2">{items.length} total · {items.filter((e) => (e.status || "new") === "new").length} new</p>
            </div>
            <div className="flex gap-2">
              <button onClick={refresh} className="border border-white/15 hover:border-white text-white px-4 py-2.5 uppercase tracking-widest text-xs font-bold inline-flex items-center gap-2"><RefreshCw size={14} /> Refresh</button>
              <button onClick={() => { authLogout(); nav("/admin"); }} data-testid="admin-logout" className="border border-white/15 hover:border-white text-white px-4 py-2.5 uppercase tracking-widest text-xs font-bold inline-flex items-center gap-2"><LogOut size={14} /> Logout</button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-6">
            {["all", ...STATUS_OPTIONS].map((s) => (
              <button key={s} onClick={() => setFilter(s)} data-testid={`admin-filter-${s}`}
                className={`px-3 py-1.5 text-xs uppercase tracking-widest font-bold ${filter === s ? "bg-silver text-black" : "border border-white/15 text-gray-400 hover:text-white"}`}>
                {s}
              </button>
            ))}
          </div>

          {loading ? <p className="text-gray-500">Loading…</p> : visible.length === 0 ? <p className="text-gray-500">No enquiries yet.</p> : (
            <div className="space-y-3">
              {visible.map((e) => (
                <div key={e.id} data-testid={`enquiry-${e.id}`} className="matte p-5 grid grid-cols-1 lg:grid-cols-12 gap-4">
                  <div className="lg:col-span-3">
                    <p className="text-white font-semibold">{e.full_name}</p>
                    <p className="font-mono-tech text-xs text-gray-400 mt-1">{e.phone}</p>
                    <p className="font-mono-tech text-xs text-gray-400 break-all">{e.email}</p>
                    <p className="font-mono-tech text-[10px] text-gray-600 mt-2">{new Date(e.created_at).toLocaleString()}</p>
                  </div>
                  <div className="lg:col-span-4 text-sm">
                    <p className="text-gray-300"><span className="text-gray-500">Vehicle:</span> {[e.vehicle_year, e.vehicle_make, e.vehicle_model].filter(Boolean).join(" ") || "—"} <span className="text-gray-500">({e.vehicle_size || "—"})</span></p>
                    <p className="text-gray-300 mt-1"><span className="text-gray-500">Service:</span> {e.service_required || "—"}</p>
                    <p className="text-gray-300 mt-1"><span className="text-gray-500">Location:</span> {e.service_type || "—"}</p>
                    <p className="text-gray-300 mt-1"><span className="text-gray-500">Date:</span> {e.preferred_date || "—"} {e.preferred_time ? `· ${e.preferred_time}` : ""}</p>
                    {e.addons?.length ? <p className="text-gray-300 mt-1"><span className="text-gray-500">Add-ons:</span> {e.addons.join(", ")}</p> : null}
                  </div>
                  <div className="lg:col-span-3 text-sm">
                    <p className="text-gray-400"><span className="text-gray-500">Estimate:</span> <span className="font-mono-tech text-silver">{e.estimated_total || "—"}</span></p>
                    <p className="text-gray-400 mt-1"><span className="text-gray-500">Type:</span> {e.enquiry_type}</p>
                    {e.condition_notes && <p className="text-gray-500 text-xs mt-2 italic">"{e.condition_notes}"</p>}
                    {e.message && <p className="text-gray-500 text-xs mt-1 italic">"{e.message}"</p>}
                  </div>
                  <div className="lg:col-span-2">
                    <select value={e.status || "new"} onChange={(ev) => changeStatus(e.id, ev.target.value)} data-testid={`status-${e.id}`}
                      className="w-full bg-[#0F0F0F] border border-white/15 text-white px-3 h-10 text-xs uppercase tracking-widest font-bold focus:outline-none focus:ring-1 focus:ring-silver">
                      {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

export function ProtectedAdmin({ children }) {
  const [status, setStatus] = useState("checking"); // checking | ok | nope
  useEffect(() => {
    if (!localStorage.getItem("speca_admin_token")) { setStatus("nope"); return; }
    authMe().then(() => setStatus("ok")).catch(() => { authLogout(); setStatus("nope"); });
  }, []);
  if (status === "checking") return <div className="pt-40 text-center text-gray-500">Verifying session…</div>;
  if (status === "nope") return <Navigate to="/admin" replace />;
  return children;
}
